import { getAppConfig } from "./appConfig";

// Per-client look, set at build time in the client's env file (client.env):
//
//   REACT_APP_BRAND_COLOR=#69152C      main colour: buttons, headings, accents
//   REACT_APP_BRAND_BG=#FCF9D6         page background (optional, defaults to a light tint)
//   REACT_APP_HEADING_FONT=Cormorant Garamond   Google Font for headings (optional)
//   REACT_APP_COVER_ART=https://.../splash.png   loading splash image (optional,
//                                                 defaults to the restaurant logo)
//
// Every shade the app uses (bg-brand-50 ... text-brand-900) is derived from
// REACT_APP_BRAND_COLOR. Nothing set = the default orange design.
const env = process.env;

const hexToRgb = (hex) => {
  const m = /^#?([0-9a-f]{6})$/i.exec((hex || "").trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

const mix = (rgb, target, amount) =>
  rgb.map((c, i) => Math.round(c + (target[i] - c) * amount));

// How far each shade moves from the brand colour toward the page background
// (+) or black (-). Light shades blend into the page background, so a dark
// brand colour on cream gives warm beiges rather than pinks or greys.
const SHADES = {
  50: 0.94, 100: 0.86, 200: 0.72, 300: 0.55, 400: 0.3,
  500: 0, 600: -0.15, 700: -0.3, 800: -0.45, 900: -0.58,
};

const shadeOf = (rgb, base, amount) => {
  if (amount >= 0) return mix(rgb, base, amount);
  // Dark brand colours are darkened less, so headings don't turn black
  const lightness = (Math.max(...rgb) + Math.min(...rgb)) / 510;
  const depth = Math.min(1, Math.max(0.4, lightness / 0.5));
  return mix(rgb, [0, 0, 0], -amount * depth);
};

const brand = hexToRgb(env.REACT_APP_BRAND_COLOR);
const pageBg = hexToRgb(env.REACT_APP_BRAND_BG);
const headingFont = env.REACT_APP_HEADING_FONT?.trim();

// Loading splash image: REACT_APP_COVER_ART (a full https:// address, or a
// path inside ClientApp/public), else the restaurant logo from Admin, else none.
export const getSplashArt = () => {
  const art = env.REACT_APP_COVER_ART?.trim();
  if (art) {
    return { src: /^https?:\/\//i.test(art) ? art : `${env.PUBLIC_URL}${art}`, isLogo: false };
  }
  const { logo } = getAppConfig();
  return logo ? { src: `${env.REACT_APP_IMAGE_BASE_URL}${logo}`, isLogo: true } : null;
};

// Called once before the app renders, so there is no flash of orange
export const applyBrandTheme = () => {
  const root = document.documentElement.style;

  if (brand) {
    const base = pageBg || [255, 255, 255];
    Object.entries(SHADES).forEach(([shade, amount]) => {
      root.setProperty(`--brand-${shade}`, shadeOf(brand, base, amount).join(" "));
    });
  }
  if (pageBg) root.setProperty("--page-bg", pageBg.join(" "));
  else if (brand) root.setProperty("--page-bg", "var(--brand-50)");

  if (headingFont) {
    root.setProperty("--heading-font", `'${headingFont}', Georgia, serif`);
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(headingFont)}:wght@600;700&display=swap`;
    document.head.appendChild(link);
  }
};
